import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import Header from './components/sections/Header';
import HeroBanner from './components/sections/HeroBanner';
import BentoSection from './components/sections/BentoSection';
import ProcesoTransparencia from './components/sections/ProcesoTransparencia';
import FAQ from './components/sections/FAQ';
import Footer from './components/sections/Footer';
import Clients from './components/sections/Clients';
import ParticlesComponent from './components/global/Particles';
import WhatsAppButton from './components/global/WhatsAppButton';
import ProjectDetails from './components/ProjectDetails';
import MeetTheTeam from './pages/MeetTheTeam';
import Modelos from './pages/Modelos';
import Servicios from "./pages/Servicios";
import IAPage from './pages/IAPage';
import Deck from './pages/Deck';
import Agendar from './pages/Agendar';
import NotFound from './pages/NotFound';
import { initAttribution } from './lib/attribution';

/** Al cambiar de ruta, siempre volver al top (evita quedar a mitad/abajo al volver). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Si hay hash (#section), dejar que la página destino maneje el ancla
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

function AppContent() {
  const footerRef = useRef(null);
  const location = useLocation();
  const isDeck = location.pathname.startsWith('/deck');

  const scrollToFooter = () => {
    footerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const showHeader = true;

  const InicioPage = () => (
    <>
      <Helmet>
        <title>eien | Desarrollo de software en Paraguay · Plataformas, Apps y SaaS</title>
        <meta name="description" content="Empresa de desarrollo de software a medida en Paraguay. Desarrollamos aplicaciones web, apps móviles, SaaS y sistemas para empresas en Asunción y LATAM. Por proyecto, partnership o revenue share." />
        <link rel="canonical" href="https://eientech.io/" />
      </Helmet>
      {/* 1 — Hero: propuesta de valor + CTA principal */}
      <section className="relative z-10">
        <HeroBanner />
      </section>
      {/* 2 — Social proof: logos de clientes (trust inmediato) */}
      <section className="relative z-10">
        <Clients />
      </section>
      {/* 3 — Servicios + Modelos: bento unificado */}
      <section className="relative z-10">
        <BentoSection />
      </section>
      {/* 4 — Proceso + Transparencia: fusionados */}
      <section className="relative z-10">
        <ProcesoTransparencia />
      </section>
      {/* 7 — FAQ: resolver objeciones */}
      <section className="relative z-10 mt-8">
        <FAQ />
      </section>
      {/* 8 — Footer + CTA final */}
      <section className="relative z-10 mt-8" id="footer" ref={footerRef}>
        <Footer />
      </section>
    </>
  );

  useEffect(() => {
    initAttribution();
  }, [location.pathname, location.search]);

  if (isDeck) {
    return (
      <Routes>
        <Route path="/deck" element={<Deck lang="es" />} />
        <Route path="/deck/es" element={<Deck lang="es" />} />
        <Route path="/deck/en" element={<Deck lang="en" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0a] transition-colors duration-300">
      {showHeader && (
        <Header onContactClick={scrollToFooter} />
      )}
      <div className={`relative z-10 App`}>
        <Routes>
          <Route path="/" element={<InicioPage />} />
          <Route path="/capacidades" element={<Servicios footerRef={footerRef}/>} />
          <Route path="/modelos" element={<Modelos footerRef={footerRef}/>} />
          <Route path="/project-details/:projectId" element={<ProjectDetails footerRef={footerRef}/>} />
          <Route path="/conocenos" element={<MeetTheTeam footerRef={footerRef}/>} />
          <Route path="/ia" element={<IAPage />} />
          <Route path="/agendar" element={<Agendar />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <WhatsAppButton />
    </div>
  );
}

function RootParticles() {
  const location = useLocation();
  // El deck es una experiencia propia: sin partículas encima
  if (location.pathname.startsWith('/deck')) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-30 opacity-50 dark:opacity-100">
      <ParticlesComponent id="tsparticles" />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <Router>
          <ScrollToTop />
          {/* Partículas al nivel raíz — nunca se desmontan por navegación */}
          <RootParticles />
          <AppContent />
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
